import { CHANNEL_OPTIONS_EXTERIOR } from '@/utils/campaignOptions'
import api from './api'
import { parseParams, transformChannelMetrics } from './common'

// -- UTILITIES FOR MOCKS --

// function wait (ms) {
//   return new Promise(resolve => {
//     setTimeout(() => resolve(), ms)
//   })
// }

// function waitAndReturn (ms, data) {
//   return wait(ms).then(() => data)
// }

export default {
  getChannel ({ id, type, ...params }) {
    if (!id || !type) {
      throw new Error('[channelService.getChannel] id and type must be present in params')
    }
    const urlParams = parseParams(params)
    return api.get(`/campaigns/channels/${id}?type=${type}&${urlParams}`).then(res => res.data)
  },
  async saveChannel (body, campaign) {
    const method = body.id ? 'put' : 'post'
    const url = body.id
      ? `/campaigns/channels/${body.id}?type=${body.channel_type}`
      : '/campaigns/channels'
    const channel = await api[method](url, body).then(res => res.data)
    const channelKey = `${body.channel_type.toLowerCase()}_channel`
    campaign[channelKey] = channel.id
    if (!body.id) {
      await api.put(`/campaigns/${campaign.id}`, {
        ...campaign,
        shops: campaign.shops.map(s => s.id)
      })
    }

    return channel
  },
  async deleteChannel ({ id, type }) {
    const url = `/campaigns/channels/${id}?type=${type}`
    return api.delete(url).then(res => res.data)
  },
  getSummary (entity, query, params, allowRequestDuplication) {
    const urlParams = parseParams(params)
    if (['products', 'targets', 'templates'].indexOf(entity) === -1) {
      throw new Error(`[channelService.getSummary] entity must be one of 'products', 'targets', 'templates'`)
    }
    const url = `/campaigns/${entity}?q=${query}&${urlParams}`
    const headers = {}
    if (allowRequestDuplication) {
      headers.allowRequestDuplication = true
    }
    return api.get(url, { headers }).then(res => res.data.results)
  },
  updateChannelKPI ({ id, kpi, goal, type }) {
    return api.put(`/campaigns/channels/${id}/kpi?type=${type}`, { kpi, goal }).then(res => res.data)
  },
  getChannelStats (channelID, type, zones = []) {
    return api.get(`/campaigns/channels/${channelID}/stats?type=${type}&zones=${zones.join(',')}`).then(res => res.data)
      .then(data => {
        return {
          count: data.items,
          budget: data.budget,
          metrics: transformChannelMetrics(data.metrics),
          zones: data.zones
        }
      })
  },
  uploadFilesHybrid ({ isNew, openingUrl, closingUrl, openingFiles, closingFiles, id, type }) {
    if (openingFiles.length !== closingFiles.length && (openingFiles.length > 0 && closingFiles.length > 0)) {
      throw new Error('[channelService.uploadFilesHybrid] open files quantity and close files quantity must be the same')
    }
    const formData = new FormData()
    formData.append('url_start', openingUrl)
    formData.append('url_end', closingUrl)
    openingFiles.forEach((f, i) => {
      if (f.file instanceof File) {
        formData.append(`file_start${i + 1}`, f.file)
      } else {
        formData.append(`file_start_id${i + 1}`, f.fileID)
      }
      formData.append(`format_start${i + 1}`, f.format.id)
    })
    closingFiles.forEach((f, i) => {
      if (f.file instanceof File) {
        formData.append(`file_end${i + 1}`, f.file)
      } else {
        formData.append(`file_end_id${i + 1}`, f.fileID)
      }
      formData.append(`format_end${i + 1}`, f.format.id)
    })

    const method = isNew ? 'post' : 'put'

    return api[method](
      `/campaigns/channels/${id}/creativities?type=${type}&frames=true`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },
  uploadFiles ({ isNew, url, files, id, type }) {
    const formData = new FormData()
    formData.append('url', url)
    files.forEach((f, i) => {
      if (f.file instanceof File) {
        formData.append(`file${i + 1}`, f.file)
      } else {
        formData.append(`file_id${i + 1}`, f.fileID)
      }
      formData.append(`format${i + 1}`, f.format.id)
    })

    const method = isNew ? 'post' : 'put'

    return api[method](
      `/campaigns/channels/${id}/creativities?type=${type}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },
  saveGoogleAPIConfig (id, apiConfig) {
    const formData = new FormData()
    formData.append('google_id', apiConfig.googleid)
    formData.append('campaign_id', apiConfig.campaignid)
    formData.append('file', apiConfig.file)

    return api.put(
      `/campaigns/channels/${id}/google`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },
  saveFBAPIConfig (id, fbid) {
    return api.put(`/campaigns/channels/${id}/facebook`, { fbid }).then(res => res.data)
  },
  saveIGAPIConfig (id, igid) {
    return api.put(`/campaigns/channels/${id}/instagram`, { igid }).then(res => res.data)
  },
  getChannelEfficiency (id, type) {
    if (type === CHANNEL_OPTIONS_EXTERIOR.EXTERIOR || type === CHANNEL_OPTIONS_EXTERIOR.SIGNALING) {
      return this.getExteriorKPIs(id, type)
    }
    return api.get(`/campaigns/channels/${id}/efficiency?type=${type}`).then(res => res.data)
  },
  getResultsCSV () {
    return api.get(`/campaigns/channels/results`, { responseType: 'arraybuffer' }).then(res => res.data)
  },
  uploadResultsCSV (id, type, file) {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/campaigns/channels/${id}/results?type=${type}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  getPreviousKPIs (body) {
    if (body.id) {
      return this.getChannelEfficiency(body.id, body.channel_type)
    }
    return api.post('/campaigns/channels/efficiency', body).then(res => res.data)
  },
  getExteriorKPIs (id, type) {
    return api.get(`/campaigns/channels/${id}/costs?type=${type}`).then(res => res.data)
  }
}
